import { createSelector } from 'reselect';
import { getCurriculum } from '../../content/curriculum';
import {
  FILL_IN_BLANK_REG_EXP,
  FILL_IN_BLANK_REG_EXP_GLOBAL,
  GENRE_CRITERIA_REG_EXP_GLOBAL,
  STORY_BLANK,
  K2_LEVEL_COUNT,
} from '../../data/constants';

/**
 * The typical use-case for a `selector` from the reselect library is to create a memo-ized function for reading the
 * Redux store. We are adapting that use case here to create "getter" functions that access the curriculum json
 * NOTE: these are not truly "selectors" because they are not memo-ized.
 */

const getCurrentCurriculum = () => getCurriculum();

/**
 * Return an object map of Schemas, indexed by schemaId
 */
export const getSchemas = createSelector(
  [getCurrentCurriculum],
  curriculum => curriculum.schemas.reduce((schemaMapById, schemaObj) => ({
    ...schemaMapById,
    [schemaObj.id]: schemaObj,
  }), {}),
);

export const getIsG35Level = levelId => levelId > K2_LEVEL_COUNT;

export const getSchema = genreId => getSchemas()[genreId];

export const getSchemaName = schemaId => getSchema(schemaId).name;

export const getSchemaCriteria = schemaId => getSchema(schemaId).criteria;

export const getLevelStructures = () => {
  const curriculum = getCurriculum();
  return curriculum.levelStructure;
};

export const getLevelStructure = levelId => getLevelStructures().find(item => item.id === levelId);

export const getLevelStructureItemsInRound = levelId => getLevelStructure(levelId).itemsInRound;

export const getQuestionCountToMasterLevel = levelId => getLevelStructure(levelId).questionCountToMaster;

/**
 * Return a list of schemaIds that are used in a given Level
 */
export const getSchemaIdsInLevel = (levelId) => {
  const schemaIds = [];
  const levelStructureItems = getLevelStructureItemsInRound(levelId);
  levelStructureItems.forEach((questionItemDetails) => {
    if (questionItemDetails.schemaId) {
      schemaIds.push(questionItemDetails.schemaId);
    }
  });
  return schemaIds;
};

export const getCreateStoryIds = schemaId => getSchema(schemaId).createStories.map(item => item.id);

export const getSortStoryIds = schemaId => getSchema(schemaId).sortStories.map(item => item.id);

export const getStory = (schemaId, storyId) => (
  getSchema(schemaId).createStories.find(item => item.id === storyId) ||
  getSchema(schemaId).sortStories.find(item => item.id === storyId)
);

export const getStoryPages = (schemaId, storyId) => getStory(schemaId, storyId).pages;

/**
 * Parse out the markup for fill-in-the-blank's and genre criteria tags from the story page raw text
 * and return a string that should be displayed to the user, either with _____ (aka, blanks) or the
 * user's previously given response for a fill-in-the-blank.
 *
 * @param {string} rawPageText - the raw, marked-up, curriculum for this page of the story
 * @param {Object} userStoryResponses - a map of fill-in-the-blank Ids to previously selected user responses
 * @returns {string}
 */
export const getStoryPageVisibleText = (rawPageText, userStoryResponses) => (
  rawPageText
    .replace(FILL_IN_BLANK_REG_EXP_GLOBAL, (fillInBlankExpression) => {
      const [, fillInBlankId] = fillInBlankExpression.match(FILL_IN_BLANK_REG_EXP);
      const userResponse = userStoryResponses[fillInBlankId];
      if (userResponse) {
        return userResponse;
      }
      return STORY_BLANK;
    })
    .replace(GENRE_CRITERIA_REG_EXP_GLOBAL, '')
);

/**
 * Parse the marked up curriculum text for the given page of a story, to return the fillInTheBlank id (if any)
 * @returns {string|undefined}
 * @example parseFillInBlankId('Hello, my name is ${your-name-here}') => 'your-name-here'
 */
export const getFillInBlankId = (storyText) => {
  const regExpMatch = FILL_IN_BLANK_REG_EXP_GLOBAL.exec(storyText);
  if (regExpMatch) {
    return regExpMatch[1]; // `exec` returns an array where element [1] is the captured string we want
  }
  return undefined;
};

// Return the possible options used to fill in the blank for the given id
export const getFillInBlankResponseOptions = (story, fillInBlankId) => (
  story.fillInTheBlankItems.find(item => parseInt(item.id, 10) === parseInt(fillInBlankId, 10)).possibleResponses
);

export const getIntroTexts = () => {
  const curriculum = getCurriculum();
  return curriculum.introTexts;
};

export const getIntroText = index => getIntroTexts()[index];

export const getOnboardingContent = () => {
  const curriculum = getCurriculum();
  return curriculum.onboardingContent;
};

export const getOnboardingStoryText = () => (
  getOnboardingContent().text
);

export const getOnboardingStoryTitle = () => (
  getOnboardingContent().title
);

export const getOnboardingStoryType = () => (
  getOnboardingContent().type
);
